export type NetworkDiagnostics = {
  online: boolean;
  webrtcAvailable: boolean;
  dataChannelAvailable: boolean;
  loopback: "pass" | "fail" | "unavailable";
  connectionState?: string;
  iceGatheringState?: string;
  candidateTypes?: string[];
  effectiveType?: string;
  error?: string;
};

export async function runNetworkDiagnostics(): Promise<NetworkDiagnostics> {
  const webrtcAvailable = typeof RTCPeerConnection !== "undefined";
  const dataChannelAvailable = webrtcAvailable && "createDataChannel" in RTCPeerConnection.prototype;
  const result: NetworkDiagnostics = {
    online: navigator.onLine,
    webrtcAvailable,
    dataChannelAvailable,
    loopback: "unavailable",
    effectiveType: readEffectiveConnectionType(),
  };

  if (!webrtcAvailable || !dataChannelAvailable) return result;

  try {
    const loopback = await runLocalDataChannelLoopback();
    result.loopback = "pass";
    result.connectionState = loopback.connectionState;
    result.iceGatheringState = loopback.iceGatheringState;
    result.candidateTypes = loopback.candidateTypes;
  } catch (error) {
    result.loopback = "fail";
    result.error = error instanceof Error ? error.message : String(error);
  }
  return result;
}

async function runLocalDataChannelLoopback(timeoutMs = 8_000): Promise<{
  connectionState: string;
  iceGatheringState: string;
  candidateTypes: string[];
}> {
  const sender = new RTCPeerConnection({ iceServers: [] });
  const receiver = new RTCPeerConnection({ iceServers: [] });
  const senderChannel = sender.createDataChannel("offline-loopback");
  let receiverChannel: RTCDataChannel | undefined;

  try {
    const openPromise = new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error("Local WebRTC channel did not open before timeout.")), timeoutMs);
      receiver.ondatachannel = (event) => {
        receiverChannel = event.channel;
        receiverChannel.onopen = () => {
          window.clearTimeout(timer);
          resolve();
        };
        receiverChannel.onmessage = (message) => {
          if (message.data === "ping") receiverChannel?.send("pong");
        };
      };
    });

    const pongPromise = new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error("Local WebRTC loopback did not return pong.")), timeoutMs);
      senderChannel.onmessage = (message) => {
        if (message.data === "pong") {
          window.clearTimeout(timer);
          resolve();
        }
      };
    });

    await sender.setLocalDescription(await sender.createOffer());
    await waitForIceGathering(sender, timeoutMs);
    await receiver.setRemoteDescription(sender.localDescription!);
    await receiver.setLocalDescription(await receiver.createAnswer());
    await waitForIceGathering(receiver, timeoutMs);
    await sender.setRemoteDescription(receiver.localDescription!);
    await openPromise;
    senderChannel.send("ping");
    await pongPromise;
    return {
      connectionState: `${sender.connectionState}/${receiver.connectionState}`,
      iceGatheringState: `${sender.iceGatheringState}/${receiver.iceGatheringState}`,
      candidateTypes: [...new Set([
        ...candidateTypesFromSdp(sender.localDescription?.sdp),
        ...candidateTypesFromSdp(receiver.localDescription?.sdp),
      ])],
    };
  } finally {
    senderChannel.close();
    receiverChannel?.close();
    sender.close();
    receiver.close();
  }
}

function readEffectiveConnectionType(): string | undefined {
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
  return connection?.effectiveType;
}

function candidateTypesFromSdp(sdp: string | undefined): string[] {
  if (!sdp) return [];
  return [...sdp.matchAll(/ typ ([a-z]+)/gi)].map((match) => match[1].toLowerCase());
}

async function waitForIceGathering(connection: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (connection.iceGatheringState === "complete") return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("ICE gathering timed out.")), timeoutMs);
    const onState = () => {
      if (connection.iceGatheringState === "complete") {
        window.clearTimeout(timer);
        connection.removeEventListener("icegatheringstatechange", onState);
        resolve();
      }
    };
    connection.addEventListener("icegatheringstatechange", onState);
  });
}
